/* global APP_VERSION, REACT_APP_PUBLIC_SENTRY_ENVIRONMENT */

import { EventParams } from './types';

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

let initialized = false;

const isDev = (): boolean => {
  try {
    return REACT_APP_PUBLIC_SENTRY_ENVIRONMENT !== 'production';
  } catch {
    return true;
  }
};

const appVersion = (): string => {
  try {
    return APP_VERSION;
  } catch {
    return '';
  }
};

const ensureGtag = (): void => {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    window.gtag = function gtag() {
      // gtag.js espera el objeto `arguments` (no un array) en el dataLayer.
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments);
    };
  }
};

// Consent Mode v2 — estado por defecto. Se setea ANTES de cargar gtag.
// Supuesto: panel B2B autenticado → analytics permitido; publicidad denegada.
// Marcar revisión legal antes de prod si aplica aviso de cookies.
export const setConsentDefault = (): void => {
  ensureGtag();
  if (typeof window === 'undefined' || !window.gtag) return;
  window.gtag('consent', 'default', {
    analytics_storage: 'granted',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });
};

export const initGA4 = (measurementId: string): void => {
  if (initialized || typeof window === 'undefined' || !measurementId) return;
  initialized = true;
  ensureGtag();

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);

  window.gtag('js', new Date());
  window.gtag('config', measurementId, {
    send_page_view: false, // pageviews SPA se mandan manualmente
    debug_mode: isDev(),
    app_version: appVersion(),
  });
};

export const ga4Event = (name: string, params?: EventParams): void => {
  if (!initialized || typeof window === 'undefined' || !window.gtag) return;
  try {
    window.gtag('event', name, params || {});
  } catch {
    // noop — no romper la cadena de tracking
  }
};

export const ga4PageView = (params: { page_path?: string; page_location?: string; page_title?: string }): void => {
  if (!initialized || typeof window === 'undefined' || !window.gtag) return;
  try {
    window.gtag('event', 'page_view', params);
  } catch {
    // noop
  }
};

export const ga4SetUser = (userId: string, userProps: EventParams): void => {
  if (!initialized || typeof window === 'undefined' || !window.gtag) return;
  try {
    window.gtag('set', { user_id: userId });
    window.gtag('set', 'user_properties', userProps);
  } catch {
    // noop
  }
};

export const setConsent = (update: Record<string, 'granted' | 'denied'>): void => {
  if (typeof window === 'undefined' || !window.gtag) return;
  try {
    window.gtag('consent', 'update', update);
  } catch {
    // noop
  }
};
